import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ElevenlabsUsageResponse } from "./types";
import { formatNum, formatUsd, userLabel } from "./helpers";

export function ElevenlabsUsagePanel() {
  const [hours, setHours] = useState<number>(24 * 7);
  const ranges: Array<{ label: string; value: number }> = [
    { label: "Last 24 hours", value: 24 },
    { label: "Last 7 days", value: 24 * 7 },
    { label: "Last 30 days", value: 24 * 30 },
    { label: "Last 90 days", value: 24 * 90 },
  ];
  const usageQuery = useQuery<ElevenlabsUsageResponse>({
    queryKey: ["/api/admin/elevenlabs-usage", hours],
    queryFn: async () => {
      const res = await fetch(`/api/admin/elevenlabs-usage?hours=${hours}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load ElevenLabs usage");
      return res.json();
    },
  });
  const data = usageQuery.data;

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold" data-testid="text-elevenlabs-usage-title">ElevenLabs Voice Usage</h3>
            <p className="text-sm text-muted-foreground">
              Per-feature voice synthesis usage for the ELEVENLABS_API_KEY secret. ElevenLabs bills per character of input text — cost estimates use the Creator-tier list price ({data ? `$${data.costPerCharacterUsd.toFixed(5)}/char` : "~$0.00030/char"}) and are a proxy, not exact billing.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Label htmlFor="select-elevenlabs-range" className="text-sm">Time range</Label>
            <Select value={String(hours)} onValueChange={(v) => setHours(parseInt(v, 10))}>
              <SelectTrigger id="select-elevenlabs-range" className="w-[180px]" data-testid="select-elevenlabs-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ranges.map((r) => (
                  <SelectItem key={r.value} value={String(r.value)} data-testid={`option-elevenlabs-range-${r.value}`}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => usageQuery.refetch()}
              disabled={usageQuery.isFetching}
              data-testid="button-refresh-elevenlabs-usage"
            >
              {usageQuery.isFetching ? "Refreshing…" : "Refresh"}
            </Button>
          </div>
        </div>
      </Card>

      {usageQuery.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground" data-testid="text-elevenlabs-usage-loading">Loading…</Card>
      ) : usageQuery.error ? (
        <Card className="p-6 text-sm text-destructive" data-testid="text-elevenlabs-usage-error">
          Failed to load ElevenLabs usage.
        </Card>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">Calls</div>
              <div className="text-2xl font-semibold" data-testid="text-elevenlabs-total-calls">{formatNum(data.totals.calls)}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {formatNum(data.totals.successes)} ok · {formatNum(data.totals.failures)} failed
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">Characters synthesized</div>
              <div className="text-2xl font-semibold" data-testid="text-elevenlabs-total-chars">{formatNum(data.totals.characters)}</div>
              <div className="text-xs text-muted-foreground mt-1">Cost proxy (ElevenLabs bills per char)</div>
            </Card>
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">Estimated cost</div>
              <div className="text-2xl font-semibold" data-testid="text-elevenlabs-total-cost">{formatUsd(data.totals.estimatedCostUsd)}</div>
              <div className="text-xs text-muted-foreground mt-1">@ ${data.costPerCharacterUsd.toFixed(5)}/char</div>
            </Card>
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">Avg latency</div>
              <div className="text-2xl font-semibold" data-testid="text-elevenlabs-avg-latency">{formatNum(data.totals.avgLatencyMs)}<span className="text-sm font-normal text-muted-foreground"> ms</span></div>
            </Card>
          </div>

          <Card className="p-4">
            <h4 className="text-sm font-semibold mb-3" data-testid="text-elevenlabs-by-feature-title">Usage by feature</h4>
            {data.byFeature.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="text-elevenlabs-by-feature-empty">No calls in this window.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Feature</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead>Voice</TableHead>
                    <TableHead className="text-right">Calls</TableHead>
                    <TableHead className="text-right">Failures</TableHead>
                    <TableHead className="text-right">Characters</TableHead>
                    <TableHead className="text-right">Avg latency</TableHead>
                    <TableHead className="text-right">Est. cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.byFeature.map((r, i) => (
                    <TableRow key={`${r.feature}-${r.modelId}-${r.voiceId}-${i}`} data-testid={`row-elevenlabs-feature-${i}`}>
                      <TableCell className="font-medium">{r.feature}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{r.modelId}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{r.voiceId}</TableCell>
                      <TableCell className="text-right">{formatNum(r.calls)}</TableCell>
                      <TableCell className="text-right">{formatNum(r.failures)}</TableCell>
                      <TableCell className="text-right">{formatNum(r.characters)}</TableCell>
                      <TableCell className="text-right">{formatNum(r.avgLatencyMs)} ms</TableCell>
                      <TableCell className="text-right font-medium">{formatUsd(r.estimatedCostUsd)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>

          <Card className="p-4">
            <h4 className="text-sm font-semibold mb-3" data-testid="text-elevenlabs-by-user-title">Top users (by characters)</h4>
            {data.byUser.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="text-elevenlabs-by-user-empty">No calls in this window.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Calls</TableHead>
                    <TableHead className="text-right">Failures</TableHead>
                    <TableHead className="text-right">Characters</TableHead>
                    <TableHead className="text-right">Est. cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.byUser.slice(0, 50).map((r, i) => (
                    <TableRow key={`${r.userId ?? "anon"}-${i}`} data-testid={`row-elevenlabs-user-${i}`}>
                      <TableCell>
                        <div className="font-medium">{userLabel(r)}</div>
                        {r.userId != null && <div className="text-xs text-muted-foreground">ID #{r.userId}</div>}
                      </TableCell>
                      <TableCell className="text-right">{formatNum(r.calls)}</TableCell>
                      <TableCell className="text-right">{formatNum(r.failures)}</TableCell>
                      <TableCell className="text-right">{formatNum(r.characters)}</TableCell>
                      <TableCell className="text-right font-medium">{formatUsd(r.estimatedCostUsd)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>

          <Card className="p-4">
            <h4 className="text-sm font-semibold mb-3" data-testid="text-elevenlabs-recent-title">Recent calls</h4>
            {data.recent.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="text-elevenlabs-recent-empty">No calls in this window.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Feature</TableHead>
                    <TableHead className="text-right">Chars</TableHead>
                    <TableHead className="text-right">Latency</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Est. cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recent.map((r) => (
                    <TableRow key={r.id} data-testid={`row-elevenlabs-recent-${r.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{new Date(r.createdAt).toLocaleString()}</TableCell>
                      <TableCell className="text-xs">{userLabel({ email: r.userEmail, displayName: r.userDisplayName, userId: r.userId })}</TableCell>
                      <TableCell className="text-xs">{r.feature}</TableCell>
                      <TableCell className="text-right">{formatNum(r.characters)}</TableCell>
                      <TableCell className="text-right">{formatNum(r.latencyMs)} ms</TableCell>
                      <TableCell>
                        {r.success ? (
                          <Badge variant="secondary" data-testid={`badge-elevenlabs-status-ok-${r.id}`}>ok</Badge>
                        ) : (
                          <Badge variant="destructive" data-testid={`badge-elevenlabs-status-fail-${r.id}`} title={r.errorMessage ?? undefined}>failed</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">{formatUsd(r.estimatedCostUsd)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </>
      ) : null}
    </div>
  );
}
